// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import * as sdk from '@canton-network/dapp-sdk';
import * as semver from 'semver';
import { GetPreferredPackageVersionResponse } from '@digitalasset/json-api-ts-client/index';
import { useConfig } from '@hooks/other/useConfig';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useJsonApiClient } from '../../../contexts/JsonApiContext';

const useIsPackageAvailableFrom: (
  packageName: string,
  parties: string[],
  availableFromVersion: string,
  enabled?: boolean,
) => UseQueryResult<boolean, string> = (
  packageName,
  parties,
  availableFromVersion,
  enabled = true,
) => {
  const jsonApi = useJsonApiClient();
  const { enableDappLogin } = useConfig().app;

  return useQuery({
    queryKey: ['queryIsAvailableFrom', packageName, parties, availableFromVersion, enableDappLogin],
    queryFn: async () => {
      const preferredPackageVersion = enableDappLogin
        ? ((await sdk.ledgerApi({
            requestMethod: 'get',
            resource: '/v2/interactive-submission/preferred-package-version',
            query: {
              parties,
              'package-name': packageName,
              vetting_valid_at: new Date(availableFromVersion),
            },
          })) as GetPreferredPackageVersionResponse)
        : await jsonApi.getV2InteractiveSubmissionPreferredPackageVersion(packageName, parties);
      const version = preferredPackageVersion.packagePreference?.packageReference?.packageVersion;
      return version ? semver.gte(version, availableFromVersion) : false;
    },
    enabled,
    refetchInterval: false,
  });
};

export default useIsPackageAvailableFrom;
